'use client';

/**
 * CreateEntityModal Component
 * Modal for creating new entities with optional template selection
 */

import { useState } from 'react';
import { useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import {
  ENTITY_TYPE_LABELS,
  ENTITY_TYPE_CATEGORIES,
  type EntityType,
} from '@/lib/command-center/types';
import TemplateSelector from './TemplateSelector';
import { type EntityTemplate, type MetadataFieldTemplate } from '@/lib/command-center/entity-templates';

interface CreateEntityModalProps {
  onClose: () => void;
  onCreated: () => void;
  defaultType?: EntityType;
  skipTemplates?: boolean;
}

type Step = 'template' | 'form';

export default function CreateEntityModal({
  onClose,
  onCreated,
  defaultType,
  skipTemplates = false,
}: CreateEntityModalProps) {
  const { createEntity } = useCommandCenter();

  const [step, setStep] = useState<Step>(skipTemplates ? 'form' : 'template');
  const [selectedTemplate, setSelectedTemplate] = useState<EntityTemplate | null>(null);

  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState<EntityType>(defaultType || 'technology');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [metadata, setMetadata] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTemplateSelect = (template: EntityTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      setEntityType(template.entityType);
      setDescription(template.defaultDescription || '');
      setTags(template.suggestedTags.join(', '));
      // Initialize metadata fields with defaults
      const initialMetadata: Record<string, string> = {};
      template.metadataFields.forEach(field => {
        if (field.defaultValue) {
          initialMetadata[field.key] = field.defaultValue;
        }
      });
      setMetadata(initialMetadata);
    }
    setStep('form');
  };

  const handleSkipTemplate = () => {
    setSelectedTemplate(null);
    setStep('form');
  };

  const handleMetadataChange = (key: string, value: string) => {
    setMetadata(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Build metadata object with non-empty values
      const finalMetadata: Record<string, unknown> = {};
      Object.entries(metadata).forEach(([key, value]) => {
        if (value && value.trim()) {
          finalMetadata[key] = value.trim();
        }
      });

      await createEntity({
        entity_type: entityType,
        name: name.trim(),
        description: description.trim() || undefined,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        metadata: Object.keys(finalMetadata).length > 0 ? finalMetadata : undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entity');
    } finally {
      setLoading(false);
    }
  };

  const renderMetadataField = (field: MetadataFieldTemplate) => {
    const value = metadata[field.key] || '';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleMetadataChange(field.key, e.target.value)}
            className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0"
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleMetadataChange(field.key, e.target.value)}
            className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleMetadataChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
          />
        );
      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleMetadataChange(field.key, e.target.value)}
            placeholder={field.placeholder || 'https://...'}
            className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
          />
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleMetadataChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-muted shrink-0">
          <div className="flex items-center gap-3">
            {step === 'form' && !skipTemplates && (
              <button
                onClick={() => setStep('template')}
                className="text-foreground-tertiary hover:text-foreground p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-xl font-semibold text-foreground">
              {step === 'template' ? 'New Entity' : selectedTemplate ? `New ${selectedTemplate.name}` : 'Create Entity'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'template' ? (
            <div className="p-4">
              <TemplateSelector
                onSelect={handleTemplateSelect}
                onSkip={handleSkipTemplate}
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Template Badge */}
              {selectedTemplate && (
                <div className="flex items-center gap-2 p-2 bg-accent-muted border border-border rounded-lg">
                  <span className="text-lg">{selectedTemplate.icon}</span>
                  <span className="text-sm text-accent">Using {selectedTemplate.name} template</span>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              {/* Entity Type */}
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-1">
                  Entity Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value as EntityType)}
                  className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0"
                  required
                >
                  {Object.entries(ENTITY_TYPE_CATEGORIES).map(([category, types]) => (
                    <optgroup key={category} label={category}>
                      {types.map((type) => (
                        <option key={type} value={type}>
                          {ENTITY_TYPE_LABELS[type]}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
                  placeholder={selectedTemplate ? `e.g., ${selectedTemplate.name} Name` : "e.g., PostgreSQL, Platform Team, SOC 2"}
                  required
                  maxLength={255}
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0 resize-none"
                  placeholder="Optional description..."
                  rows={2}
                  maxLength={2000}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
                  placeholder="Comma-separated tags"
                />
              </div>

              {/* Template-specific Metadata Fields */}
              {selectedTemplate && selectedTemplate.metadataFields.length > 0 && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <h4 className="text-sm font-medium text-foreground-secondary mb-3">
                    {selectedTemplate.name} Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedTemplate.metadataFields.map(field => (
                      <div key={field.key} className={field.type === 'url' ? 'col-span-2' : ''}>
                        <label className="block text-xs font-medium text-foreground-tertiary mb-1">
                          {field.label}
                          {field.required && <span className="text-red-400">*</span>}
                        </label>
                        {renderMetadataField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-muted">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !name.trim()}
                  className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Create Entity</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
